import { useEffect, useMemo, useState } from 'react'
import { KeyRound, LoaderCircle, RotateCcw, Shapes } from 'lucide-react'
import { toast } from 'sonner'
import { STUDENT_MASCOTS, STUDENT_SECRET_EMOJIS, normalizeStudentFullName } from '../lib/robolabx-login-catalog'
import { appendStudentSecretId, buildStudentUsernameForMascotChange } from '../lib/student-creation'
import { cn } from '../lib/utils'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { changeStudentMascot, forceUpdateOwnStudentPassword } from '../api/backend/student-credentials'
import { Button, buttonVariants } from './ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'

export const STUDENT_LOGIN_SETTINGS_LABEL = 'My Login Settings'
export const STUDENT_LOGIN_SETTINGS_TAB_LABELS = {
  mascot: 'Mascot',
  pin: 'Secret pin',
} as const

type StudentLoginSettingsTab = keyof typeof STUDENT_LOGIN_SETTINGS_TAB_LABELS

interface StudentLoginSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  studentName: string
  studentUsername?: string | null
}

export function buildStudentSelfServeMascotUsername(
  mascotId: string,
  currentUsername: string | null | undefined,
  fallbackFullName: string,
): string {
  return buildStudentUsernameForMascotChange(mascotId, currentUsername?.trim() || fallbackFullName)
}

async function submitStudentSelfServeMascotUpdate(token: string, payload: {
  mascotId: string
  fullName: string
  currentUsername?: string | null
}): Promise<void> {
  await changeStudentMascot(token, {
    memberId: 'me',
    username: buildStudentSelfServeMascotUsername(payload.mascotId, payload.currentUsername, payload.fullName),
  })
}

async function submitStudentSelfServeSecretPinUpdate(token: string, payload: {
  secretIds: string[]
  fullName: string
}): Promise<void> {
  await forceUpdateOwnStudentPassword(token, {
    newPassword: payload.secretIds.join('|'),
  })
}

export function StudentLoginSettingsDialog({
  open,
  onOpenChange,
  studentName,
  studentUsername,
}: StudentLoginSettingsDialogProps) {
  const { getAccessToken } = useAuth()
  const { refreshMember } = useWorkspace()
  const [activeTab, setActiveTab] = useState<StudentLoginSettingsTab>('mascot')
  const [selectedMascotId, setSelectedMascotId] = useState<string | null>(null)
  const [selectedSecretIds, setSelectedSecretIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const normalizedStudentName = useMemo(() => normalizeStudentFullName(studentName), [studentName])

  useEffect(() => {
    if (open) return
    setActiveTab('mascot')
    setSelectedMascotId(null)
    setSelectedSecretIds([])
    setIsSaving(false)
  }, [open])

  function handleClose() {
    if (isSaving) return
    onOpenChange(false)
  }

  function handleSecretTap(secretId: string) {
    if (isSaving) return
    setSelectedSecretIds((current) => appendStudentSecretId(current, secretId))
  }

  function handleUndoTap() {
    if (isSaving || selectedSecretIds.length === 0) return
    setSelectedSecretIds((current) => current.slice(0, -1))
  }

  async function handleSaveMascot() {
    const token = getAccessToken()
    if (!token) return
    if (!selectedMascotId) return

    setIsSaving(true)
    try {
      await submitStudentSelfServeMascotUpdate(token, {
        mascotId: selectedMascotId,
        fullName: normalizedStudentName,
        currentUsername: studentUsername,
      })
      await refreshMember()
      toast.success('Mascot updated')
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update mascot')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleSaveSecretPin() {
    const token = getAccessToken()
    if (!token) return
    if (selectedSecretIds.length !== 3) return

    setIsSaving(true)
    try {
      await submitStudentSelfServeSecretPinUpdate(token, {
        secretIds: selectedSecretIds,
        fullName: normalizedStudentName,
      })
      toast.success('Picture secret updated')
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update picture secret')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] max-w-[560px] flex-col overflow-hidden sm:max-w-[560px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{STUDENT_LOGIN_SETTINGS_LABEL}</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="grid grid-cols-2 gap-2 rounded-base border-2 border-nb-border bg-secondary-bg p-2">
            {(Object.entries(STUDENT_LOGIN_SETTINGS_TAB_LABELS) as Array<[StudentLoginSettingsTab, string]>).map(([tab, label]) => {
              const isActive = tab === activeTab
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    buttonVariants({ variant: isActive ? 'default' : 'neutral', size: 'sm' }),
                    'w-full',
                  )}
                >
                  {tab === 'mascot' ? <Shapes className="size-4" /> : <KeyRound className="size-4" />}
                  {label}
                </button>
              )
            })}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2 pr-1">
            <div
              key={activeTab}
              className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
            >
              {activeTab === 'mascot' ? (
                <div className="grid gap-4">
                  <div className="text-sm text-foreground/65">
                    This will become the animal shown in the first step of student sign in.
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {STUDENT_MASCOTS.map((mascot) => {
                      const isSelected = mascot.id === selectedMascotId
                      return (
                        <button
                          key={mascot.id}
                          type="button"
                          onClick={() => setSelectedMascotId(mascot.id)}
                          disabled={isSaving}
                          className={cn(
                            'h-[110px] w-full max-w-[110px] rounded-base border-2 p-2.5 text-left transition-all',
                            isSelected
                              ? 'border-nb-border bg-main text-main-foreground nb-shadow'
                              : 'border-nb-border bg-secondary-bg text-foreground nb-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none',
                          )}
                        >
                          <div className="flex h-full flex-col justify-between">
                            <div className="text-[1.75rem] leading-none">{mascot.emoji}</div>
                            <div className="font-heading text-[0.92rem] leading-none">{mascot.label}</div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="grid gap-4">
                  <div className="text-sm text-foreground/65">
                    Tap three emoji in order. You can undo before saving.
                  </div>
                  <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
                    {STUDENT_SECRET_EMOJIS.map((secret) => (
                      <button
                        key={secret.id}
                        type="button"
                        onClick={() => handleSecretTap(secret.id)}
                        disabled={isSaving || selectedSecretIds.length >= 3}
                        className="h-[110px] w-full max-w-[110px] rounded-base border-2 border-nb-border bg-secondary-bg p-2 text-left nb-shadow transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none disabled:opacity-50"
                      >
                        <div className="flex h-full flex-col justify-between">
                          <div className="text-[1.6rem] leading-none">{secret.emoji}</div>
                          <div className="font-heading text-[0.8rem] leading-none">{secret.label}</div>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-2">
                        {Array.from({ length: 3 }).map((_, index) => {
                          const secret = STUDENT_SECRET_EMOJIS.find((item) => item.id === selectedSecretIds[index])
                          return (
                            <div
                              key={index}
                              className="flex h-12 w-12 items-center justify-center rounded-base border-2 border-nb-border bg-secondary-bg text-2xl"
                            >
                              {secret?.emoji ?? <span className="text-lg text-foreground/30">•</span>}
                            </div>
                          )
                        })}
                    </div>

                    <button
                      type="button"
                      aria-label="Undo last tap"
                      onClick={handleUndoTap}
                      disabled={selectedSecretIds.length === 0 || isSaving}
                      className={cn(buttonVariants({ variant: 'neutral', size: 'icon' }), 'shrink-0')}
                    >
                      <RotateCcw className="size-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void (activeTab === 'mascot' ? handleSaveMascot() : handleSaveSecretPin())}
            disabled={isSaving || (activeTab === 'mascot' ? !selectedMascotId : selectedSecretIds.length !== 3)}
          >
            {isSaving ? <LoaderCircle className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
